import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { ShieldCheck, ShieldAlert, ChevronDown, ChevronUp } from "lucide-react";

/**
 * WarrantyCard
 * - Muestra el estado de garantía de la orden y sus ciclos.
 * - Permite abrir un nuevo ciclo de garantía cuando la orden está en "Retirado por cliente" (DELIVERED).
 * - No sobrescribe datos originales: crea un nuevo ciclo dentro de la MISMA orden.
 */
export default function WarrantyCard({ order, canOpenWarranty, onOpened }) {
  const [showForm, setShowForm] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [reason, setReason] = useState("");
  const [reportedFailure, setReportedFailure] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const cycles = order.warranty_cycles || [];
  const isEligible = order.status === "DELIVERED";
  const isInWarranty = order.status === "IN_WARRANTY";
  const currentCycle = order.current_cycle || "original";
  const isWarrantyCycle = String(currentCycle).startsWith("warranty_");

  // Vencimiento de garantía (30 días desde el último DELIVERED)
  const daysRemaining = order.warranty_days_remaining;
  const isActive = order.warranty_active === true;
  const isExpired = daysRemaining !== null && daysRemaining !== undefined && daysRemaining <= 0;
  const deadlineAt = order.warranty_deadline_at;

  // Estilo del chip según proximidad
  let warrantyChip = null;
  if (deadlineAt) {
    const deadlineStr = new Date(deadlineAt).toLocaleDateString("es-AR");
    if (isExpired) {
      const overdue = Math.abs(daysRemaining || 0);
      warrantyChip = {
        cls: "bg-red-100 text-red-800 border-red-300",
        label: `Garantía vencida hace ${overdue} día${overdue !== 1 ? "s" : ""}`,
        subtext: `Venció el ${deadlineStr}`,
      };
    } else if (daysRemaining <= 7) {
      warrantyChip = {
        cls: "bg-amber-100 text-amber-800 border-amber-300",
        label: `Vence en ${daysRemaining} día${daysRemaining !== 1 ? "s" : ""}`,
        subtext: `Hasta el ${deadlineStr}`,
      };
    } else {
      warrantyChip = {
        cls: "bg-emerald-100 text-emerald-800 border-emerald-300",
        label: `Vence en ${daysRemaining} días`,
        subtext: `Hasta el ${deadlineStr}`,
      };
    }
  }

  async function openWarranty() {
    if (!reason.trim() || reason.trim().length < 3) {
      toast.error("El motivo es obligatorio (mín. 3 caracteres)");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/orders/${order.id}/warranty/open`, {
        reason: reason.trim(),
        reported_failure: reportedFailure.trim(),
        notes: notes.trim(),
      });
      toast.success(`Garantía #${cycles.length + 1} abierta`);
      setReason("");
      setReportedFailure("");
      setNotes("");
      setShowForm(false);
      await onOpened?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card-base p-4" data-testid="warranty-card">
      <button
        className="w-full flex items-center justify-between"
        onClick={() => setExpanded((v) => !v)}
        data-testid="warranty-toggle"
      >
        <div className="flex items-center gap-2">
          {isInWarranty ? (
            <ShieldAlert size={18} className="text-indigo-600" />
          ) : (
            <ShieldCheck size={18} className="text-slate-500" />
          )}
          <h3 className="font-heading font-bold text-lg">Garantía</h3>
          {cycles.length > 0 && (
            <span className="chip bg-indigo-100 text-indigo-700 border-indigo-200" data-testid="warranty-cycle-count">
              {cycles.length} ciclo{cycles.length > 1 ? "s" : ""}
            </span>
          )}
        </div>
        {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>

      {!expanded ? null : (
        <div className="mt-3 space-y-3">
          {/* Chip de vencimiento (30 días desde último Retirado por cliente) */}
          {warrantyChip && (
            <div
              className={`rounded-xl border px-3 py-2 flex items-start gap-2 ${warrantyChip.cls}`}
              data-testid="warranty-deadline-chip"
            >
              <ShieldAlert size={16} className="shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <div className="font-bold" data-testid="warranty-deadline-label">{warrantyChip.label}</div>
                <div className="text-xs opacity-80" data-testid="warranty-deadline-subtext">{warrantyChip.subtext}</div>
              </div>
            </div>
          )}

          {/* Ciclo original */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <div className="text-xs font-mono uppercase tracking-wider text-slate-500">Ciclo actual</div>
            <div className="font-semibold text-slate-800" data-testid="warranty-current-cycle-label">
              {isWarrantyCycle ? `Garantía #${cycles.length}` : "Reparación original"}
            </div>
            {isWarrantyCycle && (
              <div className="text-xs text-indigo-700 mt-1">
                Los datos del ciclo original permanecen intactos. El nuevo diagnóstico, fotos y observaciones se guardan en este ciclo.
              </div>
            )}
          </div>

          {/* Historial de ciclos */}
          {cycles.length > 0 && (
            <div className="space-y-2" data-testid="warranty-cycles-list">
              <div className="text-xs font-mono uppercase tracking-wider text-slate-500">Ciclos de garantía</div>
              {cycles.map((c) => (
                <div
                  key={c.id || c.cycle_number}
                  className="rounded-lg border border-indigo-200 bg-indigo-50 p-3"
                  data-testid={`warranty-cycle-${c.cycle_number}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-indigo-800">Garantía #{c.cycle_number}</div>
                    <div className="text-xs text-indigo-700 font-mono">
                      {c.opened_at ? new Date(c.opened_at).toLocaleString("es-AR") : ""}
                    </div>
                  </div>
                  <div className="text-sm text-slate-700 mt-1">
                    <b>Motivo:</b> {c.reason || "—"}
                  </div>
                  {c.reported_failure && (
                    <div className="text-sm text-slate-700 mt-0.5">
                      <b>Falla reportada:</b> {c.reported_failure}
                    </div>
                  )}
                  {c.notes && (
                    <div className="text-sm text-slate-600 mt-0.5">
                      <b>Notas:</b> {c.notes}
                    </div>
                  )}
                  <div className="text-xs text-slate-500 mt-1">
                    Registrada por {c.opened_by_name || "—"}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Acciones */}
          {isEligible && canOpenWarranty && !showForm && !isExpired && (
            <button
              onClick={() => setShowForm(true)}
              data-testid="warranty-open-btn"
              className="w-full py-2.5 px-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm transition-colors flex items-center justify-center gap-2"
            >
              <ShieldAlert size={16} /> Registrar garantía
            </button>
          )}

          {isEligible && isExpired && (
            <div
              className="w-full py-2.5 px-3 rounded-xl bg-slate-100 text-slate-500 font-semibold text-sm text-center border border-slate-200 cursor-not-allowed"
              data-testid="warranty-expired-btn"
              title="La ventana de 30 días ya pasó"
            >
              🔒 Garantía vencida — no se puede registrar
            </div>
          )}

          {!isEligible && !isInWarranty && canOpenWarranty && (
            <div className="text-xs text-slate-500 italic">
              Podés registrar una garantía cuando la orden esté en estado <b>"Retirado por cliente"</b>.
            </div>
          )}

          {showForm && (
            <div className="rounded-xl border-2 border-indigo-300 bg-white p-3 space-y-3" data-testid="warranty-form">
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-slate-500">
                  Motivo por el que el cliente regresa <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Ej: Drift nuevamente en joystick izquierdo"
                  data-testid="warranty-reason-input"
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300 focus:border-indigo-500 focus:outline-none text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-slate-500">
                  Falla reportada en garantía
                </label>
                <textarea
                  value={reportedFailure}
                  onChange={(e) => setReportedFailure(e.target.value)}
                  placeholder="Detalles adicionales de la falla reportada por el cliente"
                  rows={2}
                  data-testid="warranty-failure-input"
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300 focus:border-indigo-500 focus:outline-none text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-slate-500">
                  Observaciones
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  data-testid="warranty-notes-input"
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300 focus:border-indigo-500 focus:outline-none text-sm"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={openWarranty}
                  disabled={saving}
                  data-testid="warranty-confirm-btn"
                  className="flex-1 py-2 px-3 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-semibold text-sm"
                >
                  {saving ? "Registrando…" : "Confirmar garantía"}
                </button>
                <button
                  onClick={() => setShowForm(false)}
                  disabled={saving}
                  data-testid="warranty-cancel-btn"
                  className="py-2 px-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-sm"
                >
                  Cancelar
                </button>
              </div>
              <div className="text-xs text-slate-500 italic">
                Se reabrirá esta misma orden con un nuevo ciclo. No se crea una orden nueva ni se duplica el cliente. Los datos originales permanecen intactos.
              </div>
            </div>
          )}

          {isInWarranty && (
            <div className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg p-2">
              Esta orden está actualmente <b>en garantía</b>. Puede avanzar en el flujo normal (Diagnóstico → Reparación → …) hasta cerrar nuevamente en <b>"Retirado por cliente"</b>.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
